import {
  getPurchaseRealtimeToken,
  resolvePurchaseRealtimeWebsocketUrl,
} from '../api/notificationApi';
import { notifyProductEvent } from './productEventsHub';

const listeners = new Set();
let socket = null;
let currentScope = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let connectionGeneration = 0;
let isOpening = false;

const clearReconnect = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const notifyListeners = (payload) => {
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error('Error in purchase event listener:', error);
    }
  });
};

const closeSocket = () => {
  if (!socket) return;
  const previousSocket = socket;
  socket = null;
  try {
    previousSocket.close();
  } catch (error) {
    console.error('Error closing purchase WebSocket:', error);
  }
};

const scheduleReconnect = () => {
  clearReconnect();
  if (!currentScope) return;
  const delay = Math.min(15000, 1000 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    openSocket().catch((error) => {
      console.error('Error reconnecting purchase WebSocket:', error);
    });
  }, delay);
};

const openSocket = async () => {
  if (!currentScope || isOpening) return;
  const generation = connectionGeneration;
  const scope = currentScope;
  isOpening = true;
  clearReconnect();

  let config;
  try {
    config = await getPurchaseRealtimeToken(scope.branchId, scope.companyId);
  } catch (error) {
    if (generation !== connectionGeneration) return;
    console.error(
      'Error fetching purchase realtime token:',
      error?.response?.data?.message || error?.message || error
    );
    scheduleReconnect();
    return;
  } finally {
    if (generation === connectionGeneration) isOpening = false;
  }

  if (generation !== connectionGeneration || currentScope?.key !== scope.key) return;
  if (
    String(config?.companyId) !== scope.companyId
    || String(config?.branchId) !== scope.branchId
    || !config?.token
  ) {
    console.error('Purchase realtime token scope did not match the selected company and branch.');
    scheduleReconnect();
    return;
  }

  const websocketUrl = resolvePurchaseRealtimeWebsocketUrl(
    config.websocketUrl || `/ws2/${scope.branchId}`,
    config.token
  );
  const nextSocket = new WebSocket(websocketUrl);
  socket = nextSocket;

  nextSocket.onopen = () => {
    if (socket !== nextSocket || generation !== connectionGeneration) return;
    const recoveredConnection = reconnectAttempts > 0;
    reconnectAttempts = 0;
    if (recoveredConnection) {
      notifyProductEvent({
        type: 'products.invalidated',
        action: 'reconnected',
        companyId: scope.companyId,
        branchId: scope.branchId,
        reason: 'realtime-reconnected',
      });
      notifyListeners({ type: 'purchases.resync', companyId: scope.companyId, branchId: scope.branchId });
    }
  };

  nextSocket.onmessage = (event) => {
    if (socket !== nextSocket || generation !== connectionGeneration) return;
    try {
      const payload = JSON.parse(event.data);
      if (payload?.type === 'products.invalidated') {
        if (String(payload.companyId) !== scope.companyId
          || String(payload.branchId) !== scope.branchId) return;
        // Bridge once per websocket frame, not once per mounted consumer.
        notifyProductEvent(payload);
      }
      notifyListeners(payload);
    } catch (error) {
      console.error('Error handling purchase WebSocket message:', error);
    }
  };

  nextSocket.onclose = () => {
    if (socket === nextSocket) socket = null;
    if (generation !== connectionGeneration || currentScope?.key !== scope.key) return;
    scheduleReconnect();
  };
};

export const startPurchaseEvents = ({ branchId, companyId } = {}) => {
  const normalizedBranchId = String(branchId || '').trim();
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedBranchId || !normalizedCompanyId) {
    stopPurchaseEvents();
    return Promise.resolve();
  }

  const key = `${normalizedCompanyId}:${normalizedBranchId}`;
  if (currentScope?.key === key && (socket || isOpening)) return Promise.resolve();

  currentScope = {
    key,
    companyId: normalizedCompanyId,
    branchId: normalizedBranchId,
  };
  reconnectAttempts = 0;
  connectionGeneration += 1;
  isOpening = false;
  clearReconnect();
  closeSocket();
  return openSocket();
};

export const stopPurchaseEvents = () => {
  currentScope = null;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  isOpening = false;
  clearReconnect();
  closeSocket();
};

export const subscribePurchaseEvents = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
